import { useFetchEQLFields } from '@modules/common/hooks';
import {
  BatchExecuteEqlExpression,
  BatchExecuteEqlQuery,
  useBatchExecuteEqlQuery,
} from 'generated/graphql';
import { useMemo } from 'react';
import { AndOfErrors } from 'v2/core/errors/errors';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import {
  AndStatuses,
  BatchExecuteEQLResponse,
  CreateSkipWithStatus,
  EdgeResponse,
  QueryOptions,
  QueryStatus,
  UseBatchExecuteEQLInput,
} from 'v2/core/repository/edge-server/types';
import { mapToEQL } from 'v2/core/repository/eql/mappers';

const useBatchExecuteEQL = (
  input: UseBatchExecuteEQLInput,
  options?: QueryOptions & {
    onCompleted?: (data: BatchExecuteEqlQuery) => void;
    onError?: (error: Error) => void;
  },
): EdgeResponse<BatchExecuteEQLResponse> => {
  // TODO: Refactor this. Idealy a gateway should only call one API.
  const {
    data: sourceDetailsMap,
    status: sStatus,
    error: sError,
  } = useGetSourceDetailsMap();

  const { fields: eqlFields, isLoading: isLoadingEqlFields } =
    useFetchEQLFields();

  const expressions = useMemo(
    (): BatchExecuteEqlExpression[] | undefined =>
      sourceDetailsMap
        ? input.batches.map((batch) => ({
            id: batch.id,
            expression: mapToEQL({
              ...batch.eqlProps,
              sourceDetailsMap,
              eqlFields,
            }),
          }))
        : undefined,
    [input.batches, eqlFields, sourceDetailsMap],
  );

  const skip = CreateSkipWithStatus(options?.Skip ?? false, sStatus);

  const {
    data,
    loading,
    error: eError,
    called,
  } = useBatchExecuteEqlQuery({
    variables: {
      tenantID: input.tenantID,
      expressions: expressions ?? [],
    },
    fetchPolicy: options?.FetchPolicy,
    skip,
    onCompleted: options?.onCompleted,
    onError: options?.onError,
  });

  const eStatus = apolloStatusToQueryStatus(loading, called, skip, eError);
  const eqlFieldsStatus = apolloStatusToQueryStatus(isLoadingEqlFields, true);

  const response = data?.batchExecuteEQL?.results?.reduce(
    (response: BatchExecuteEQLResponse, result) => {
      response[result.queryID] = result.result;
      return response;
    },
    {} as BatchExecuteEQLResponse,
  );

  const status = AndStatuses([eqlFieldsStatus, sStatus, eStatus]);
  const error = AndOfErrors([sError, eError]);

  return {
    data:
      data || status === QueryStatus.SUCCESS_COMPLETED ? response : undefined,
    error,
    status,
  };
};

export default useBatchExecuteEQL;
